import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    IconButton,
    Typography,
    alpha,
    makeStyles,
    useTheme,
} from '@material-ui/core';
import { Pause, PlayArrow } from '@material-ui/icons';
import { formatAudioTime } from 'src/components/AudioPlayer/utils';
import {
    buildPlaceholderWaveformLevels,
    extractWaveformLevelsFromUrl,
} from 'src/views/OmniChannel/utils/audioRecorder';
import { AudioWaveform } from './AudioWaveform';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        width: '100%',
        minWidth: 0,
    },
    playButton: {
        padding: 4,
        color: 'inherit',
        flexShrink: 0,
    },
    waveform: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
    },
    duration: {
        fontSize: 12,
        whiteSpace: 'nowrap',
        color: 'inherit',
        flexShrink: 0,
    },
}));

type TInlineAudioPlayerProps = {
    src: string;
    waveformLevels?: number[];
    activeColor?: string;
    inactiveColor?: string;
};

const waitForAudioReady = (audio: HTMLAudioElement) =>
    new Promise<void>((resolve, reject) => {
        if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
            resolve();
            return;
        }

        const handleReady = () => {
            cleanup();
            resolve();
        };

        const handleError = () => {
            cleanup();
            reject(new Error('Audio preview failed to load'));
        };

        const cleanup = () => {
            audio.removeEventListener('canplay', handleReady);
            audio.removeEventListener('error', handleError);
        };

        audio.addEventListener('canplay', handleReady);
        audio.addEventListener('error', handleError);
        audio.load();
    });

export const InlineAudioPlayer = ({
    src,
    waveformLevels,
    activeColor,
    inactiveColor,
}: TInlineAudioPlayerProps) => {
    const classes = useStyles();
    const theme = useTheme();
    const resolvedActiveColor = activeColor ?? theme.palette.text.primary;
    const resolvedInactiveColor =
        inactiveColor ?? alpha(theme.palette.text.primary, 0.45);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [levels, setLevels] = useState<number[]>(
        waveformLevels?.length
            ? waveformLevels
            : buildPlaceholderWaveformLevels()
    );

    useEffect(() => {
        const audio = new Audio();
        audio.crossOrigin = 'anonymous';
        audio.preload = 'auto';
        audio.src = src;
        audioRef.current = audio;

        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);

        const handleLoadedMetadata = () => setDuration(audio.duration || 0);
        const handleTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(audio.duration || 0);
        };

        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleEnded);
        audio.load();

        return () => {
            audio.pause();
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('ended', handleEnded);
            audioRef.current = null;
        };
    }, [src]);

    useEffect(() => {
        let cancelled = false;

        if (waveformLevels?.length) {
            setLevels(waveformLevels);
            return undefined;
        }

        setLevels(buildPlaceholderWaveformLevels());

        void extractWaveformLevelsFromUrl(src)
            .then((decodedLevels) => {
                if (!cancelled) {
                    setLevels(decodedLevels);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setLevels(buildPlaceholderWaveformLevels());
                }
            });

        return () => {
            cancelled = true;
        };
    }, [src, waveformLevels]);

    useEffect(() => {
        if (!isPlaying) return undefined;

        let frameId = 0;

        const tick = () => {
            const audio = audioRef.current;

            if (audio) {
                setCurrentTime(audio.currentTime || 0);
            }

            frameId = requestAnimationFrame(tick);
        };

        frameId = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(frameId);
        };
    }, [isPlaying]);

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    const handleSeek = (ratio: number) => {
        const audio = audioRef.current;

        if (!audio || !duration) return;

        const nextTime = ratio * duration;
        audio.currentTime = nextTime;
        setCurrentTime(nextTime);
    };

    const togglePlay = async () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
            return;
        }

        if (audio.currentTime >= (audio.duration || 0)) {
            audio.currentTime = 0;
            setCurrentTime(0);
        }

        try {
            await waitForAudioReady(audio);
            await audio.play();
            setIsPlaying(true);
        } catch {
            setIsPlaying(false);
        }
    };

    return (
        <Box className={classes.root}>
            <IconButton
                className={classes.playButton}
                onClick={() => {
                    void togglePlay();
                }}
                aria-label={isPlaying ? 'pause-audio' : 'play-audio'}
            >
                {isPlaying ? (
                    <Pause fontSize="small" />
                ) : (
                    <PlayArrow fontSize="small" />
                )}
            </IconButton>

            <Box className={classes.waveform}>
                <AudioWaveform
                    levels={levels}
                    progress={progress}
                    activeColor={resolvedActiveColor}
                    inactiveColor={resolvedInactiveColor}
                    scrubberColor={resolvedActiveColor}
                    showScrubber
                    onSeek={duration > 0 ? handleSeek : undefined}
                />
            </Box>

            <Typography className={classes.duration} variant="caption">
                {formatAudioTime(
                    isPlaying || currentTime > 0 ? currentTime : duration
                )}
            </Typography>
        </Box>
    );
};
